import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import { useHistory } from "react-router-dom";
import { Spinner } from "reactstrap";
import { Trans } from "@lingui/react";
import Button from "./Button";

import request from "@/utils/request";
import api from "@/utils/api";
import { RebateCode, StripeUiMode } from "@/types";
import {
  getBrowserFlags,
  getCountryCode,
  getErrorMessageFromApiError,
  readConfigurationProperty,
} from "@/utils/common";
import { useAppDispatch, useAppSelector } from "@/hooks/hook";
import { setAccountId } from "@/reducers/stripeSlice";
import StripeCheckout from "@/components/StripeCheckout";

interface Props {
  rebateCodeId: string;
}

interface CheckoutResponse {
  Url?: string;
  ClientSecret?: string;
  StripeAccount?: string;
  Status?: string;
}

export const RebateCodePayment: React.FC<Props> = ({ rebateCodeId }) => {
  const [checkout, setCheckout] = useState<CheckoutResponse | undefined>();
  const [paymentProviderId, setPaymentProviderId] = useState<number | undefined>();
  const [error, setError] = useState<string | undefined>();
  const dispatch = useAppDispatch();
  const { push } = useHistory();
  const customerEmail = useAppSelector((s) => s.customer.data?.customer.Email);
  const configuration = useAppSelector((s) => s.configuration.data);

  const payWithQvickly = paymentProviderId === 3;
  const payWithStripe = paymentProviderId === 5;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!configuration) {
          throw new Error("No application configuration found.");
        }

        if (!customerEmail) {
          throw new Error("No customer email found.");
        }

        const rebateResponse = await request(
          api.fetchRebateCodes({
            Id: rebateCodeId,
          })
        );
        const rebateCode = rebateResponse.data.Results?.[0] as RebateCode | undefined;

        if (!rebateCode) {
          throw new Error("No rebate code found.");
        }

        if (!rebateCode.Article?.Id || !rebateCode.Article?.ArticleTypeId) {
          throw new Error("Rebate code article is missing.");
        }

        const companyResponse = await request(
          api.getCompanySettings({
            Id: rebateCode.CompanyId,
          })
        );
        const providerId =
          companyResponse.data.Results?.[0]?.BookingSettings?.PaymentProviderId;

        if (providerId !== 3 && providerId !== 5) {
          throw new Error("Unsupported payment provider.");
        }
        setPaymentProviderId(providerId);

        const { iOSSafari } = getBrowserFlags();
        const uiMode: StripeUiMode =
          !configuration.paymentButton && !iOSSafari ? "embedded" : "hosted";

        const checkoutResponse = await request(
          api.createCheckout(
            {
              Id: rebateCode.Id,
              CompanyId: rebateCode.CompanyId,
              PaymentProviderId: providerId,
              CountryCode: getCountryCode(readConfigurationProperty("language")),
              Articles: [
                {
                  InternalReferenceId: rebateCode.Id,
                  ArticleTypeId: rebateCode.Article.ArticleTypeId,
                  Quantity: 1,
                },
              ],
              UiMode: uiMode,
              customerEmail,
            },
            configuration
          )
        );

        const checkoutData = checkoutResponse.data as CheckoutResponse;
        if (checkoutData.StripeAccount) {
          dispatch(setAccountId(checkoutData.StripeAccount));
        }
        setCheckout(checkoutData);
      } catch (err: any) {
        setError(getErrorMessageFromApiError(err));
      }
    };

    fetchData();
  }, [rebateCodeId, configuration, customerEmail, dispatch]);

  useEffect(() => {
    if (payWithStripe && checkout?.Url && !checkout.ClientSecret && window.top?.location) {
      window.top.location = checkout.Url;
    }
  }, [checkout, payWithStripe]);

  return (
    <div className="Payment">
      {checkout ? (
        <>
          {payWithStripe && checkout.ClientSecret ? (
            <StripeCheckout clientSecret={checkout.ClientSecret} />
          ) : null}
          {payWithQvickly && checkout.Url ? (
            <iframe
              src={checkout.Url}
              width="100%"
              height="810px"
              frameBorder="0"
              data-testid="billmateContainer"
              title="Billmate"
            />
          ) : null}
        </>
      ) : error ? (
        <div className="container p-2 text-center">
          <p>
            <Trans id="payment.followingErrorHasOccured" values={{ bookingId: rebateCodeId }} />
          </p>
          <p className="text-danger">{error}</p>
          <Button
            primary
            onClick={() => {
              push("/");
              window.location.reload();
            }}
          >
            <Trans id="reload" />
          </Button>
        </div>
      ) : (
        <div className="Payment__loading">
          <Spinner color="secondary" />
        </div>
      )}
    </div>
  );
};

interface PageProps extends RouteComponentProps<{ rebateCodeId: string }> {}

const RebateCodePaymentPage: React.FC<PageProps> = (props) => (
  <RebateCodePayment rebateCodeId={props.match.params.rebateCodeId} />
);

export default RebateCodePaymentPage;
